import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pill } from '@/features/feeds/components/FeedsNav'
import { ErrorNote, GrowthFrame, Segmented, money, num, useSelection } from '../components/GrowthFrame'
import { useSearchTerms } from '../hooks/useGrowth'

// The miner's query classes (lib/ads/classify.js), in plain words.
export const CLASS_LABEL: Record<string, string> = {
  product_style: 'Product / style',
  text_intent: 'Ideas & how-to',
  artist: 'Artist',
  branded: 'Branded',
  competitor: 'Competitor',
  off_aesthetic: 'Off-aesthetic',
  other: 'Other',
  unclassified: 'Not yet classified',
}

const SORTS = [
  { value: 'cost', label: 'Spend' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'impressions', label: 'Impr.' },
  { value: 'purchases', label: 'Purchases' },
]

export function SearchTermsPage() {
  const sel = useSelection()
  const [text, setText] = useState(sel.params.get('q') || '')
  const campaign = sel.params.get('campaign') || undefined
  const cls = sel.params.get('class') || undefined
  const sort = sel.params.get('sort') || 'cost'
  const noSale = sel.params.get('noSale') === 'true'
  useEffect(() => {
    const t = setTimeout(() => sel.set({ q: text || null }), 300)
    return () => clearTimeout(t)
  }, [text]) // eslint-disable-line react-hooks/exhaustive-deps
  const q = useSearchTerms(sel.account?.customerId, { days: sel.days, campaign, q: sel.params.get('q') || undefined, class: cls, sort, noSale, limit: 200 })
  const cur = sel.account?.currency || 'USD'
  const classes = q.data?.classes || []
  const totalCost = classes.reduce((s, c) => s + c.cost, 0)

  return (
    <GrowthFrame
      title="Search terms"
      description="What people typed before an ad showed, from the nightly sync (runs to yesterday). The class is the miner's read of intent; negatives it proposes appear in Feeds → Proposals."
    >
      {q.error && <ErrorNote error={q.error} />}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Spend by intent</CardTitle>
          <CardDescription>Click a class to filter. {campaign && <>Filtered to one campaign · <button className="underline" onClick={() => sel.set({ campaign: null })}>all campaigns</button></>}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {classes.map((c) => (
            <button
              key={c.class}
              type="button"
              onClick={() => sel.set({ class: cls === c.class ? null : c.class })}
              className={`grid w-full grid-cols-[10rem_1fr_auto] items-center gap-3 rounded px-1 py-0.5 text-left text-sm hover:bg-muted/60 ${cls === c.class ? 'bg-muted' : ''}`}
            >
              <span>{CLASS_LABEL[c.class] || c.class}</span>
              <span className="h-2 rounded-full bg-muted">
                <span className="block h-2 rounded-full bg-foreground/60" style={{ width: `${totalCost ? (c.cost / totalCost) * 100 : 0}%` }} />
              </span>
              <span className="w-64 text-right text-xs tabular-nums text-muted-foreground">
                {money(c.cost, cur)} · {num(c.clicks)} clicks · {num(c.purchases)} purch. · {num(c.terms)} terms
              </span>
            </button>
          ))}
          {!classes.length && !q.isLoading && <p className="text-sm text-muted-foreground">No search terms in this window.</p>}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Filter terms…" className="h-8 w-64" />
        <Segmented label="Sort" value={sort} options={SORTS} onChange={(v) => sel.set({ sort: v })} />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={noSale} onChange={(e) => sel.set({ noSale: e.target.checked ? 'true' : null })} />
          Spent with no purchase
        </label>
        {cls && <Pill tone="blue">{CLASS_LABEL[cls] || cls} <button className="ml-1" onClick={() => sel.set({ class: null })} aria-label="Clear class">×</button></Pill>}
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {!q.data ? <Skeleton className="h-64" /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Search term</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="text-right">Impr.</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">CPC</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Purchases</TableHead>
                  <TableHead>Proposal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.data.map((t) => (
                  <TableRow key={t.term}>
                    <TableCell className="font-medium">{t.term}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{CLASS_LABEL[t.class] || t.class}{t.room ? ` · ${t.room}` : ''}</TableCell>
                    <TableCell className="max-w-56 truncate text-xs text-muted-foreground" title={t.campaigns.join(', ')}>{t.campaigns.join(', ')}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(t.impressions)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(t.clicks)}</TableCell>
                    <TableCell className="text-right tabular-nums">{t.cpc != null ? money(t.cpc, cur) : '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(t.cost, cur)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(t.purchases)}</TableCell>
                    <TableCell>{t.proposal ? <Pill tone={t.proposal === 'applied' ? 'green' : t.proposal === 'rejected' ? 'slate' : 'amber'}>{t.proposal}</Pill> : null}</TableCell>
                  </TableRow>
                ))}
                {!q.data.data.length && <TableRow><TableCell colSpan={9} className="text-center text-sm text-muted-foreground">No matching terms.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </GrowthFrame>
  )
}
